import { EColors } from "@atoms/ZColors/interface";
import {
	hasDuplicateAddressInList,
	shippingAddressAddHandler
} from "@/V2Utils";
import {
	getShippingAddressRowText,
	getMatchedAddressIndexInList
} from "@/Utils";
import { ZSelect, ZTag } from "@atoms";
import { Row, Col } from "antd";
import ZTypography from "@atoms/ZTypography";
import React, { useEffect, useState } from "react";
import { Address, ChildEntityHydrationParams } from "@/Contracts";
import AddShippingAddressDrawer from "@pages/Drawers/Clients/ShippingAddress/index";
import useDispatch from "@/Components/Utils/useDispatch";
import ClientsActionCreators from "@/Actions/Clients";
import {
	ClientType,
	EditClientRequest
} from "@/Contracts/Clients";
import {
	ShippingAddressSelectionProps,
	ShippingAddressesListItemsProps
} from "./interface";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import "./index.less";
import ZMessage from "@molecules/ZMessage";

export const getShippingAddressesListItems = ({
	address,
	id,
	isDisabled = false,
	isDocumentLevel = false
}: ShippingAddressesListItemsProps) => {
	const addressText = getShippingAddressRowText(address);
	const name = (
		<Row
			className={`h-full mr-2 ${isDisabled ? "disabled" : ""}`}
			data-testid={`addess-list-item-${id}`}
		>
			<Col className="flex items-center">
				<ZTypography.ZText
					level={isDocumentLevel ? 1.5 : 1}
					className="inline-block truncate"
				>
					{addressText}
				</ZTypography.ZText>
				<ZTypography.ZText
					level={isDocumentLevel ? 1.5 : 1}
					className={`ml-1 ${
						isDocumentLevel ? "ship-to-address-default-tag" : ""
					}`}
				>
					{address?.is_default && (
						<ZTag color={EColors.BLUE}>Default</ZTag>
					)}
				</ZTypography.ZText>
			</Col>
		</Row>
	);
	return {
		name,
		value: id
	};
};

export const useShippingAddressSelectionHook = ({
	currentActiveClient: selectedClient,
	showAddShippingAddress = false,
	handleClientUpdate,
	defaultShippingAddress,
	selectDefaultFromClient = true,
	isFeatureEnabled,
	isDisabled = false,
	isDocumentLevel = true
}: ShippingAddressSelectionProps) => {
	const dispatch = useDispatch();

	const { currentActiveClient } = useSelector((state: GlobalState) => ({
		currentActiveClient: state.clients.currentActiveClient
	}));

	const [selectedShippingAddress, setSelectedShippingAddress] =
		useState<Address | null>(null);

	const [isAddNewAddressVisible, setIsAddNewAddressVisible] =
		useState<boolean>(false);

	const [selectedShippingAddressIndex, setSelectedShippingAddressIndex] =
		useState<string | undefined>();

	const [newShippingAddressIndex, setNewShippingAddressIndex] = useState<
		number | undefined
	>();

	const [shippingAddressList, setShippingAddressList] = useState<Address[]>(
		[]
	);

	const setDefaultShippingAddressIndex = () => {
		const addressList = selectedClient?.shipping_addresses?.slice() ?? [];
		let shippingAddressIndex = -1;
		setShippingAddressList(addressList);
		if (defaultShippingAddress) {
			const index = getMatchedAddressIndexInList(
				addressList,
				defaultShippingAddress
			);
			if (index > -1) {
				shippingAddressIndex = index;
			} else {
				defaultShippingAddress.is_default = false;
				shippingAddressIndex = addressList.length;
				addressList.push(defaultShippingAddress);
				setShippingAddressList(addressList);
			}
		} else {
			shippingAddressIndex = selectDefaultFromClient
				? addressList.findIndex(address => address.is_default)
				: -1;
		}
		if (newShippingAddressIndex !== undefined) {
			shippingAddressIndex = newShippingAddressIndex;
			setNewShippingAddressIndex(undefined);
		}
		if (addressList.length == 0) {
			shippingAddressIndex = -1;
		}
		setSelectedShippingAddress(addressList[shippingAddressIndex] ?? null);
		setSelectedShippingAddressIndex(
			shippingAddressIndex >= 0
				? shippingAddressIndex.toString()
				: undefined
		);
	};

	useEffect(() => {
		isFeatureEnabled && setDefaultShippingAddressIndex();
	}, [
		defaultShippingAddress,
		selectedClient?.shipping_addresses,
		selectedClient?.id
	]);

	const shippingAddressesListItems = shippingAddressList.map((address, idx) =>
		getShippingAddressesListItems({
			address,
			id: idx.toString(),
			isDisabled,
			isDocumentLevel
		})
	);
	const showAddNewAddress = () => {
		setIsAddNewAddressVisible(true);
	};
	const handleShippingAddressChange = (
		selectedShippingAddressIndex: string
	) => {
		const shippingAddress: Address =
			shippingAddressList[parseInt(selectedShippingAddressIndex)] ?? null;
		setSelectedShippingAddressIndex(selectedShippingAddressIndex);
		setSelectedShippingAddress(shippingAddress ?? null);
	};

	const closeAddShippingAddressDrawer = () => {
		setIsAddNewAddressVisible(false);
	};

	const handleAddShippingAddress = async (newShippingAddress: Address) => {
		if (!selectedClient) {
			return;
		}
		const clientShipToAddresses = selectedClient.shipping_addresses ?? [];
		if (
			hasDuplicateAddressInList(clientShipToAddresses, newShippingAddress)
		) {
			ZMessage.error("Shipping address already exist!");
			return;
		}
		const { updatedShippingAddresses, selectedIndex } =
			shippingAddressAddHandler(
				newShippingAddress,
				clientShipToAddresses
			);
		let editClientRequest: EditClientRequest = {
			id: selectedClient.id,
			billing_address: selectedClient.billing_address ?? new Address(),
			shipping_addresses:
				updatedShippingAddresses ?? new Array<Address>(),
			includedChildEntities: [],
			company_type: selectedClient.company_type ?? ClientType.Prospect,
			name: selectedClient.name,
			owner_user_id: selectedClient.owner_user_id,
			fax: selectedClient.fax ?? "",
			website: selectedClient.website ?? "",
			linked_in_url: selectedClient.linked_in_url ?? null,
			facebook_url: selectedClient.facebook_url ?? null,
			twitter_url: selectedClient.twitter_url ?? null,
			market: selectedClient.market ?? null,
			source: selectedClient.source ?? null,
			campaign: selectedClient.campaign ?? null,
			tax_region_id: selectedClient.tax_region_id,
			date_acquired: selectedClient.date_acquired,
			mandatory_custom_field_check_require: true
		};

		try {
			const includedChildEntitites =
				new Array<ChildEntityHydrationParams>(
					ChildEntityHydrationParams.OPPORTUNITIES_ALL,
					ChildEntityHydrationParams.CLIENT_USERS_ALL,
					ChildEntityHydrationParams.DOCUMENTS_ALL
				);
			editClientRequest = {
				...editClientRequest,
				includedChildEntities: [...includedChildEntitites]
			};
			dispatch(ClientsActionCreators.showClientsLoader);
			const { editClient } = await dispatch(
				ClientsActionCreators.editClient(editClientRequest)
			);
			if (currentActiveClient?.id === editClient.id) {
				dispatch(
					ClientsActionCreators.updateClientDetails({
						...currentActiveClient,
						...editClient
					})
				);
			}
			setNewShippingAddressIndex(selectedIndex);
			handleClientUpdate?.(editClient);
			closeAddShippingAddressDrawer();
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(ClientsActionCreators.hideClientsLoader);
		}
	};

	const AddNewShippingAddressDrawer =
		selectedClient && showAddShippingAddress && isFeatureEnabled ? (
			<AddShippingAddressDrawer
				visible={isAddNewAddressVisible}
				onClose={closeAddShippingAddressDrawer}
				shippingAddresses={selectedClient?.shipping_addresses ?? []}
				onSubmit={handleAddShippingAddress}
			/>
		) : null;

	const ShippingAddresSelection = isFeatureEnabled ? (
		<ZSelect
			selectOptions={shippingAddressesListItems}
			className="w-full shipping-address-zselect"
			onChange={handleShippingAddressChange}
			value={selectedShippingAddressIndex}
			data-testid="shipping-address-selection"
			placeholder={
				isDocumentLevel
					? "Please select a shipping address"
					: "No shipping addresses added"
			}
			disabled={isDisabled}
			allowClear={isDocumentLevel}
			getPopupContainer={trigger => trigger.parentNode}
			dropdownRender={menu => {
				return (
					<>
						{menu}
						{showAddShippingAddress && (
							<div
								className="add-entity-action"
								onMouseDown={$event => {
									$event.preventDefault();
								}}
								onClick={showAddNewAddress}
							>
								Add shipping address
							</div>
						)}
					</>
				);
			}}
		/>
	) : null;
	return {
		ShippingAddresSelection,
		selectedShippingAddress: selectedShippingAddress ?? null,
		AddShippingAddressDrawer: AddNewShippingAddressDrawer
	};
};
